import { useState, useEffect, useRef, useCallback } from 'react';
import { PartyClient, type RoomState, type ViewType } from '../party';

interface UsePartyOptions {
  roomId: string;
  userId: string;
  username: string;
  avatar: string;
}

export function useParty(options: UsePartyOptions | null) {
  const clientRef = useRef<PartyClient | null>(null);
  const [state, setState] = useState<RoomState>({
    view: 'graph',
    selectedEntity: null,
    filters: {},
    leader: null,
    participants: {},
    annotations: [],
  });

  useEffect(() => {
    if (!options) return;

    const client = new PartyClient(
      options.roomId,
      options.userId,
      options.username,
      options.avatar
    );
    clientRef.current = client;

    const unsub = client.subscribe(setState);
    client.connect();

    return () => {
      unsub();
      client.disconnect();
      clientRef.current = null;
    };
  }, [options?.roomId, options?.userId]);

  const changeView = useCallback((view: ViewType) => {
    clientRef.current?.changeView(view);
  }, []);

  const selectEntity = useCallback((entity: { type: string; id: string } | null) => {
    clientRef.current?.selectEntity(entity);
  }, []);

  const toggleLead = useCallback(() => {
    clientRef.current?.toggleLead();
  }, []);

  const addAnnotation = useCallback((entityType: string, entityId: string, note: string) => {
    clientRef.current?.addAnnotation(entityType, entityId, note);
  }, []);

  const updateFilters = useCallback((filters: Record<string, any>) => {
    clientRef.current?.updateFilters(filters);
  }, []);

  return {
    state,
    changeView,
    selectEntity,
    toggleLead,
    addAnnotation,
    updateFilters,
  };
}
